import { QuestionCircleOutlined } from "@ant-design/icons";
import { Divider, Form, Input, Select, Tooltip } from "antd";
import axios from "axios";
import React, { ReactElement, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { responseNotification } from "../../utils/notify";
import ImageUploader from "../common/ImageUploader";
import SubmitResetBtn from "../common/SubmitBtn";
import { useServiceTypeOptions } from "../../hooks/useServiceTypeOptions";

const AddUser = ({ UserDetails, onCloseMethod }: any): ReactElement => {
  const { token, type: initialType } = useSelector(
    (state: any) => state?.authReducer
  );
  const [form] = Form.useForm();

  const { serviceTypeOptions, getServiceTypeOptions } = useServiceTypeOptions();

  const [type, setType] = useState(initialType);
  const [loading, setLoading] = useState(false);
  const [adminGroup, setAdminGroup] = useState([]);
  const [adminData, setAdminData] = useState<any>();
  const [userGroup, setUserGroup] = useState<string[]>([]);
 
  const [generatedImgUrl, setGeneratedImgUrl] = useState<string>();

  const [groupOptions, setGroupOptions] = useState({
    loading: false,
    list: [],
  });

  const [startUpload, setStartUpload] = useState<
    "Initiated" | "Uploading" | "Uploaded"
  >();

  /**
   * Fetch User Details (Edit Mode)
   */
  const fetchCorporateDetails = useCallback(async () => {
    if (!UserDetails?.merchantId) return;

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_RIDER_API}/sub-merchant-details`,
        {
          params: { merchantId: UserDetails?.merchantId },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.data?.statusCode === 200) {
        const admin = res.data?.merchant;
        const groups =
          res.data?.groups?.map((group: any) => group?.groupId) || [];

        setAdminData(admin);
        setUserGroup(groups);
        setGeneratedImgUrl(admin?.profilePicture);

        form.setFieldsValue({
          name: admin?.fullName,
          email: admin?.email,
          mobileNumber: admin?.mobileNumber?.replace("+88", ""),
          serviceType: admin?.serviceType,
          groupId: groups,
        });
      }
    } catch (error) {
      console.error(error);
      responseNotification("Failed to load user data", "error");
    }
  }, [UserDetails, token, form]);

  /**
   * Fetch Groups
   */
  const fetchUserRoleGroupDetails = async () => {
    if (UserDetails?.merchantId) {
      await fetch(
        `${process.env.REACT_APP_AUTH_API}/admin/merchant/get-merchant-groups?merchantId=${adminData?.merchantId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      )
        .then((res) => res.json())
        .then((res) => {
          if (res.statusCode === 200) {
            form.resetFields();
            setAdminGroup(
              res?.merchantUserGroups?.map((group: { groupId: any }) => group?.groupId)
            );
          } else {
            responseNotification(res.message || "something wrong", "warning");
          }
        })
        .catch((err) => {
          responseNotification(`${"Internal server error"} ${err}`, "error");
          console.error("err", err);
        });
    }
  };

  const getGroupOptions = useCallback(async () => {
    try {
      setGroupOptions({ loading: true, list: [] });

      const res = await axios.get(
        `${process.env.REACT_APP_AUTH_API}/merchant-user-group/my-user-groups`,
        {
          params: { type },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setGroupOptions({
        loading: false,
        list: res.data?.merchantUserGroups?.map((group: any) => ({
          label: group?.merchantGroup?.name,
          value: group?.merchantGroup?.id,
        })),
      });
    } catch (err) {
      console.error(err);
      setGroupOptions({ loading: false, list: [] });
    }
  }, [type, token]);

  /**
   * Submit Form
   */
  const onSubmit = async (data: any) => {
    setLoading(true);

    const readyData = data && {
      name: data.name,
      email: data.email,
      mobileNumber: `+88${
        data?.contactNumber?.replace("+88", "") ||
        data?.mobileNumber?.replace("+88", "")
      }`,
      password: data.password,
      serviceType: type,
      merchantGroupActives: userGroup.map((item) => ({
        groupId: item,
        isActive: true,
      })),
      profilePicture: generatedImgUrl ? generatedImgUrl : data?.bannerImage,
    };

    if (adminData) {
      await fetch(`${process.env.REACT_APP_RIDER_API}/update`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...readyData,
          merchantId: adminData?.merchantId,
        }),
      })
        .then((res) => res.json())
        .then((res) => {
          setLoading(false);

          if (res.statusCode === 200) {
            responseNotification("Updated Successfully", "success");
            form.resetFields();
            if (onCloseMethod) {
              onCloseMethod();
            }
          } else if (res.status === 500) {
            responseNotification("Internal server error", "error");
          } else {
            responseNotification(res.message || "something wrong", "warning");
          }
        })
        .catch((err) => {
          setLoading(false);
          responseNotification(`${"Internal server error"} ${err}`, "error");
          console.error("err", err);
        });
    } else {
      await fetch(`${process.env.REACT_APP_RIDER_API}/merchant/registration`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(readyData),
      })
        .then((res) => res.json())
        .then((res) => {
          setLoading(false);

          if (res.statusCode === 200) {
            responseNotification("Create Successfully", "success");
            form.resetFields();
            if (onCloseMethod) {
              onCloseMethod();
            }
          } else if (res.status === 500) {
            responseNotification("Internal server error", "error");
          } else {
            responseNotification(res.message || "something wrong", "warning");
          }
        })
        .catch((err) => {
          setLoading(false);
          responseNotification(`${"Internal server error"} ${err}`, "error");
          console.error("err", err);
        });
    }
  };

  /**
   * Upload finished
   */
  useEffect(() => {
    if (startUpload === "Uploaded") {
      form.submit();
    }
  }, [startUpload, form]);

  /**
   * Init Calls
   */
  useEffect(() => {
    getServiceTypeOptions();
    getGroupOptions();
  }, [getServiceTypeOptions, getGroupOptions]);

  useEffect(() => {
    fetchCorporateDetails();
  }, [fetchCorporateDetails]);
  useEffect(() => {
    if (adminData){
      fetchUserRoleGroupDetails();
    } 
  }, [adminData]);
  useEffect(() => {
    if (adminData || adminGroup) {
      form.resetFields();
    }
  }, [adminData, adminGroup, UserDetails]);
  return (
    <Form form={form} 
    layout="vertical" 
    onFinish={onSubmit} 
    autoComplete="off"
    initialValues={{
      ...adminData,
      name: adminData?.fullName,
      groupId: adminGroup,
    }}
    >
      <Form.Item
        name="name"
        label="User Name"
        rules={[{ required: true, message: "Name is required" }]}
      >
        <Input placeholder="Enter Name" />
      </Form.Item>

      <Form.Item
        name="email"
        label="Email"
        rules={[{ required: true, type: "email" }]}
      >
        <Input placeholder="Enter Email" />
      </Form.Item>

      <Form.Item
        name="mobileNumber"
        label="Mobile Number"
        rules={[{ required: true }]}
      >
        <Input addonBefore="+88" placeholder="Enter Mobile Number" />
      </Form.Item>
      {!adminData && (
        <>
          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: true }]}
          >
            <Input.Password placeholder="Enter Password" />
          </Form.Item>
        </>
      )}

      <Form.Item name="serviceType" label="Service Type">
        <Select
          options={serviceTypeOptions.list}
          loading={serviceTypeOptions.loading}
          onChange={(val) => setType(val)}
          placeholder="Select Service Type"
        />
      </Form.Item>

      <Form.Item name="groupId" label="Groups">
        <Select
          mode="multiple"
          value={userGroup}
          onChange={(val) => setUserGroup(val)}
          options={groupOptions.list}
          loading={groupOptions.loading}
          placeholder="Select Group"
        />
      </Form.Item>

      <Divider orientation="left">
        Image
        <Tooltip title="Image should be square (200x200)">
          <QuestionCircleOutlined className="px-2" />
        </Tooltip>
      </Divider>

      <ImageUploader
        imgUrl={generatedImgUrl || ""}
        startUpload={startUpload}
        setStartUpload={setStartUpload}
        setGeneratedImgUrl={setGeneratedImgUrl}
        crop={1}
      />

      <SubmitResetBtn
        onClickSubmit={(e) => {
          e.preventDefault();
          setStartUpload("Uploading");
        }}
        onClickReset={() => {
          form.resetFields();
          setGeneratedImgUrl(undefined);
          setUserGroup([]);
        }}
        loading={loading}
      />
    </Form>
  );
};

export default AddUser;
